import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Col, Nav, Row, Dropdown } from "react-bootstrap";
import { FaFlag, FaTimes, FaThLarge, FaTable } from "react-icons/fa";
import { STATEMENT_STATUS, deriveStatementStatus } from "../constants/statementStatus";
import { BsFilter } from "react-icons/bs";

import useFetchData from "../components/useFetchDatav2";
import { useToast } from "../Context/useToast";
import { format, parseISO } from "date-fns";
import styled from "styled-components";
import InvoiceStatementModal from "../components/InvoiceStatementModal";
import InvoiceStatementSetupModal from "../components/InvoiceStatementModalSetup";
import { useLocation, useNavigate } from "react-router-dom";
import InvoiceStatementImage from "../components/InvoiceStatementImage";
import "./InvoiceStatements.css";
import { filterInvoiceStatements } from "../utils/filterInvoiceStatements";
import InvoiceStatementsTable from "../components/InvoiceStatementsTable";

// Display-only relabel of backend `sup_state_type` values. The Sphere SQL emits
// "Consolidated" for statements spanning multiple locations; we surface it as
// "Enterprise" in the UI. Raw values are kept for grouping/filter logic.
const LOCATION_TYPE_LABELS = { Consolidated: "Enterprise" };
const formatLocationTypeLabel = (value) => LOCATION_TYPE_LABELS[value] ?? value;

const MonthHeader = styled.div`
   width: 100%;
   background-color: #fbfcfe;
   padding: 16px 0 10px 0;
   margin-top: 16px;
   border-bottom: 1px solid #dee2e6;
   flex-basis: 100%;

   h5 {
      margin-bottom: 0;
   }

   &:first-child {
      margin-top: 0;
      padding-top: 10px;
   }
`;

const ImagesContainer = styled.div`
   display: flex;
   flex-wrap: wrap;
   justify-content: start;
   gap: 12px;
   padding-bottom: 16px;
`;

const CustomDropdownToggle = styled(Dropdown.Toggle)`
   && {
      background-color: rgba(255, 255, 255, 1) !important;
      border: none !important;
      width: 32px !important;
      height: 32px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      padding: 0 !important;
      border-radius: 50% !important;
      box-shadow: none !important;
   }
`;

const IconItem = styled.div`
   .icon-item {
      height: 100%;
   }
`;

const CollapsiblePanel = styled.div`
   position: fixed;
   right: 0;
   top: 0;
   height: 100vh;
   background-color: #f8f9fa;
   border-left: 1px solid #dee2e6;
   transition: width 0.3s ease-in-out;
   z-index: 1050;
`;

// Status icon set + derivation live in ../constants/statementStatus.js so the tile
// grid and the table view render the identical icon/colour. Don't fork them here.
const StatusIcon = React.memo(({ statementState, reviewSeverity, invoiceCount, matchCount, hasFollowUps }) => {
   const meta = STATEMENT_STATUS[deriveStatementStatus({ statementState, invoiceCount, matchCount })];
   if (!meta) return null;
   const { icon: Icon, color, title } = meta;

   return (
      <>
         <span title={`${title} (${matchCount || 0}/${invoiceCount || 0})`}>
            <Icon color={color} size={22} />
         </span>
         {hasFollowUps && statementState !== "SETUP" && (
            <span className="ms-2">
               <FaFlag size={16} color="orange" title="Follow-ups pending" />
            </span>
         )}
      </>
   );
});

const InvoiceStatementIcon = React.memo(({ activeSection, payloadInvoiceStatements, appliedFilters, selectedInvoiceStatementKey, onImageClick, headerRefs }) => {
   const groupedData = useMemo(() => {
      const filteredStatements = filterInvoiceStatements(payloadInvoiceStatements, activeSection, appliedFilters);

      if (activeSection === "recent" || activeSection === "setup") {
         return { __all__: filteredStatements };
      } else if (activeSection === "location") {
         return filteredStatements.reduce((acc, file) => {
            const key = file.sup_state_type || "Unknown Location";
            if (!acc[key]) acc[key] = [];
            acc[key].push(file);
            return acc;
         }, {});
      } else {
         return filteredStatements.reduce((acc, file) => {
            let key = "Unknown Period";
            if (file.sup_state_frmt_statement_date) {
               const parsed = parseISO(file.sup_state_frmt_statement_date);
               if (!isNaN(parsed)) {
                  key = format(parsed, "MMM yyyy");
               }
            }
            if (!acc[key]) acc[key] = [];
            acc[key].push(file);
            return acc;
         }, {});
      }
   }, [payloadInvoiceStatements, activeSection, appliedFilters]);

   const renderImages = (groupKey, files) =>
      files.map((file) => {
         const imageIndex = `${groupKey}-${file.doc_part_id}`;
         const isDisabled = file.disabled === true;
         const handleClick = isDisabled
            ? undefined
            : () =>
                 onImageClick(
                    groupKey,
                    imageIndex,
                    file.inv_stat_id,
                    file.doc_part_id,
                    file.sm_name,
                    file.sup_state_frmt_statement_date,
                    file.doc_id
                 );

         return (
            <InvoiceStatementImage
               key={imageIndex}
               alt="Invoice Statement"
               docId={file.doc_id}
               docPartId={file.doc_part_id}
               supName={file.sm_name}
               custName={file.cust_name}
               invoiceCount={file.invoice_count}
               invoiceMatchCount={file.invoice_state_match}
               hasFollowUps={file.has_active_follow_ups}
               statementState={file.statement_state}
               reviewSeverity={file.dpa_review_severity}
               supplier_type={formatLocationTypeLabel(file.sup_state_type)}
               supplier_month={file.sup_state_frmt_statement_date}
               active_section={activeSection}
               urgencyBucket={file.terms_urgency_bucket}
               dueDate={file.terms_due_date}
               onClick={handleClick}
               disabled={isDisabled}
               isSelected={selectedInvoiceStatementKey === imageIndex}
               StatusIcon={StatusIcon}
            />
         );
      });

   if (activeSection === "recent" || activeSection === "setup") {
      return (
         <ImagesContainer className="mt-5">
            {renderImages("__all__", groupedData.__all__ ?? [])}
         </ImagesContainer>
      );
   }

   return (
      <div className="icon-container d-flex flex-wrap justify-content-start gap-3 mt-5">
         {Object.keys(groupedData).map((groupKey) => (
            <MonthHeader key={groupKey} ref={(el) => (headerRefs.current[groupKey] = el)} style={{ width: "100%" }}>
               <h5 className="fs-3 mb-2">{formatLocationTypeLabel(groupKey)}</h5>
               <ImagesContainer>
                  {renderImages(groupKey, groupedData[groupKey])}
               </ImagesContainer>
            </MonthHeader>
         ))}
      </div>
   );
});

export default function InvoiceStatements() {
   const location = useLocation();
   const navigate = useNavigate();
   const [months, setMonths] = useState([]);
   const [activeSection, setActiveSection] = useState(location.state?.activeSection || "recent");
   const [payloadInvoiceStatements, setPayloadInvoiceStatements] = useState([]);
   const [startIndex, setStartIndex] = useState(0);
   const { fetchData } = useFetchData();
   const { triggerToast } = useToast();

   const [selectedDocumentPartId, setSelectedDocumentPartId] = useState(location.state?.selectedDocumentPartId || null);
   const [selectedInvoiceStatementId, setSelectedInvoiceStatementId] = useState(null);
   
   const [selectedInvoiceStatementSupName, setSelectedInvoiceStatementSupName] = useState(null);
   const [selectedInvoiceStatementDate, setSelectedInvoiceStatementDate] = useState(null);
   const [selectedDocId, setSelectedDocId] = useState(null);
   const [openFilterMenu, setOpenFilterMenu] = useState(null);
   const [appliedFilters, setAppliedFilters] = useState({
      dateRange: { from: "", to: "" },
      locations: [],
      suppliers: [],
      years: [],
   });
   const [draftFilters, setDraftFilters] = useState({
      dateRange: { from: "", to: "" },
      locations: [],
      suppliers: [],
      years: [],
   });
   
   const headerRefs = useRef({});
   const hasRestoredRef = useRef(false);
   const [selectedInvoiceStatementKey, setselectedInvoiceStatementKey] = useState(null);
   const [activeGroupHeading, setActiveGroupHeading] = useState(null);
   const activeGroupRef = useRef(null);
   const rafIdRef = useRef(null);
   const [listRefreshTick, setListRefreshTick] = useState(0);

   const [viewMode, setViewMode] = useState(() => {
      try { return localStorage.getItem("invStatementsViewMode") || "statement"; } catch { return "statement"; }
   });
   useEffect(() => {
      try { localStorage.setItem("invStatementsViewMode", viewMode); } catch { /* ignore */ }
   }, [viewMode]);

   const [isLoadingStatements, setIsLoadingStatements] = useState(false);

   const filteredStatements = useMemo(
      () => filterInvoiceStatements(payloadInvoiceStatements, activeSection, appliedFilters),
      [payloadInvoiceStatements, activeSection, appliedFilters]
   );

   const tableAllowed = ["recent", "unreconciled", "reconciled", "location", "month"].includes(activeSection);
   const showTable = tableAllowed && viewMode === "table";

   useEffect(() => {
      const stickyBottom = 220;

      const computeActiveHeading = () => {
         const refs = headerRefs.current;
         let current = null;
         let highestElement = null;
         let highestTop = -Infinity;

         Object.entries(refs).forEach(([key, el]) => {
            if (!el) return;
            const rect = el.getBoundingClientRect();
            
            // Check if heading is above the sticky threshold and is the highest one
            if (rect.top < stickyBottom && rect.top > highestTop) {
               highestTop = rect.top;
               highestElement = key;
            }
         });

         current = highestElement;

         if (current !== activeGroupRef.current) {
            activeGroupRef.current = current;
            setActiveGroupHeading(current);
         }
      };

      const handleScroll = () => {
         if (rafIdRef.current) return;
         rafIdRef.current = requestAnimationFrame(() => {
            computeActiveHeading();
            rafIdRef.current = null;
         });
      };

      window.addEventListener("scroll", handleScroll, { passive: true });
      computeActiveHeading();

      return () => {
         window.removeEventListener("scroll", handleScroll);
         if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      };
   }, [payloadInvoiceStatements, activeSection]);

   useEffect(() => {
      if (!location.state) return;
      if (hasRestoredRef.current) return;

      if (location.state.activeSection) {
         setActiveSection(location.state.activeSection);
      }

      if (location.state.selectedInvoiceStatementId) {
         setSelectedInvoiceStatementId(location.state.selectedInvoiceStatementId);
      }

      if (location.state.selectedDocumentPartId) {
         setSelectedDocumentPartId(location.state.selectedDocumentPartId);
      }


      hasRestoredRef.current = true;
   }, [location.state]);

   const scrollToHeader = useCallback((monthYear) => {
      const el = headerRefs.current[monthYear];
      if (!el) return;

      const headerOffset = 71;
      const elementPosition = el.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;

      window.scrollTo({
         top: offsetPosition,
         behavior: "smooth",
      });
   }, []);

   const handleImageClick = useCallback(
      (monthYear, imageIndex, invStatId, docPartId, supName, statementDate, docId) => {
         if (selectedInvoiceStatementKey !== imageIndex) {
            setselectedInvoiceStatementKey(imageIndex);
            setSelectedInvoiceStatementId(invStatId ?? null);
            setSelectedDocumentPartId(docPartId ?? null);
            setSelectedInvoiceStatementSupName(supName ?? null);
            setSelectedInvoiceStatementDate(statementDate ?? null);
            setSelectedDocId(docId ?? null);
         }
      },
      [selectedInvoiceStatementKey]
   );

   const handleSectionChange = useCallback((section) => {
      setActiveSection(section);
      setSelectedDocumentPartId(null);
      setSelectedDocId(null);
      setselectedInvoiceStatementKey(null);
      setAppliedFilters({ dateRange: { from: "", to: "" }, locations: [], suppliers: [], years: [] });
      setDraftFilters({ dateRange: { from: "", to: "" }, locations: [], suppliers: [], years: [] });
      window.scrollTo({ top: 0, behavior: "auto" });
   }, []);

   const reconcileFilterOptions = useMemo(() => {
      const statementsWithState = payloadInvoiceStatements.filter((file) => {
         const hasInvStatId = file?.inv_stat_id != null && file?.inv_stat_id !== "";
         return hasInvStatId || (file?.statement_state && file.statement_state !== "SETUP");
      });

      const locationSet = new Set();
      const supplierNameSet = new Set();
      const yearSet = new Set();
      const dateValues = [];

      statementsWithState.forEach((file) => {
         if (file?.sup_state_frmt_statement_date) {
            const parsed = parseISO(file.sup_state_frmt_statement_date);
            if (!isNaN(parsed)) {
               dateValues.push(format(parsed, "yyyy-MM-dd"));
               yearSet.add(format(parsed, "yyyy"));
            }
         }

         if (file?.sup_state_type) {
            locationSet.add(file.sup_state_type);
         }

         if (file?.sm_name) {
            supplierNameSet.add(file.sm_name);
         }
      });

      const sortedDates = dateValues.sort();
      const today = format(new Date(), "yyyy-MM-dd");

      return {
         location: Array.from(locationSet).sort(),
         supplierNames: Array.from(supplierNameSet).sort(),
         years: Array.from(yearSet).sort().reverse(),
         defaultDateRange: {
            from: sortedDates[0] || today,
            to: sortedDates[sortedDates.length - 1] || today,
         },
      };
   }, [payloadInvoiceStatements]);

   useEffect(() => {
      setAppliedFilters((prev) => ({
         ...prev,
         dateRange: reconcileFilterOptions.defaultDateRange,
      }));

      setDraftFilters((prev) => ({
         ...prev,
         dateRange: reconcileFilterOptions.defaultDateRange,
      }));
   }, [reconcileFilterOptions.defaultDateRange]);

   const formatFilterDate = useCallback((value) => {
      if (!value) return "";
      try {
         return format(parseISO(value), "yyyy-M-d");
      } catch {
         return value;
      }
   }, []);

   const dateRangePillLabel = useMemo(() => {
      const { from, to } = appliedFilters.dateRange;
      if (!from || !to) return "Date range: Any";
      return `Date range: ${formatFilterDate(from)}-${formatFilterDate(to)}`;
   }, [appliedFilters.dateRange, formatFilterDate]);

   const severityPillLabel = useMemo(() => {
      if (appliedFilters.suppliers.length === 0) return "Supplier Name: Any";
      if (appliedFilters.suppliers.length <= 3) {
         return `Supplier Name: ${appliedFilters.suppliers.join(", ")}`;
      }
      return `Supplier Name: ${appliedFilters.suppliers.slice(0, 3).join(", ")}, +${appliedFilters.suppliers.length - 3}`;
   }, [appliedFilters.suppliers]);

   const locationPillLabel = useMemo(() => {
      if (appliedFilters.locations.length === 0) return "Location: Any";
      if (appliedFilters.locations.length <= 2) {
         return `Location: ${appliedFilters.locations.map(formatLocationTypeLabel).join(", ")}`;
      }
      return `Location: ${appliedFilters.locations.slice(0, 2).map(formatLocationTypeLabel).join(", ")}, +${appliedFilters.locations.length - 2}`;
   }, [appliedFilters.locations]);

   const yearPillLabel = useMemo(() => {
      if (appliedFilters.years.length === 0) return "Year: Any";
      if (appliedFilters.years.length <= 3) {
         return `Year: ${appliedFilters.years.join(", ")}`;
      }
      return `Year: ${appliedFilters.years.slice(0, 3).join(", ")}, +${appliedFilters.years.length - 3}`;
   }, [appliedFilters.years]);

   const dateRangeIsActive = useMemo(() => {
      const { from, to } = appliedFilters.dateRange;
      const { from: defFrom, to: defTo } = reconcileFilterOptions.defaultDateRange;
      return !!(from && to && (from !== defFrom || to !== defTo));
   }, [appliedFilters.dateRange, reconcileFilterOptions.defaultDateRange]);

   const openFilterPanel = useCallback(
      (filterName) => {
         setDraftFilters({
            dateRange: { ...appliedFilters.dateRange },
            locations: [...appliedFilters.locations],
            suppliers: [...appliedFilters.suppliers],
            years: [...appliedFilters.years],
         });
         setOpenFilterMenu((prev) => (prev === filterName ? null : filterName));
      },
      [appliedFilters]
   );

   const resetSingleFilter = useCallback(
      (filterName) => {
         if (filterName === "dateRange") {
            setDraftFilters((prev) => ({
               ...prev,
               dateRange: { ...reconcileFilterOptions.defaultDateRange },
            }));
            return;
         }

         setDraftFilters((prev) => ({
            ...prev,
            [filterName]: [],
         }));
      },
      [reconcileFilterOptions.defaultDateRange]
   );

   const toggleDraftMultiValue = useCallback((filterName, value) => {
      setDraftFilters((prev) => {
         const existingValues = prev[filterName] || [];
         const nextValues = existingValues.includes(value)
            ? existingValues.filter((item) => item !== value)
            : [...existingValues, value];

         return {
            ...prev,
            [filterName]: nextValues,
         };
      });
   }, []);

   const applyOpenFilter = useCallback(() => {
      if (!openFilterMenu) return;

      setAppliedFilters((prev) => ({
         ...prev,
         [openFilterMenu]: Array.isArray(draftFilters[openFilterMenu])
            ? [...draftFilters[openFilterMenu]]
            : { ...draftFilters[openFilterMenu] },
      }));

      setOpenFilterMenu(null);
   }, [draftFilters, openFilterMenu]);

   const resetAllFilters = useCallback(() => {
      const resetState = {
         dateRange: { ...reconcileFilterOptions.defaultDateRange },
         locations: [],
         suppliers: [],
         years: [],
      };

      setAppliedFilters(resetState);
      setDraftFilters(resetState);
      setOpenFilterMenu(null);
   }, [reconcileFilterOptions.defaultDateRange]);

   const getMonthYearKey = (file, activeSection) => {
      if (activeSection === "recent" || activeSection === "setup") {
         return "__all__";
      }
      if (activeSection === "location") {
         return file.sup_state_type || "Unknown Location";
      }
      const rawDate = file?.sup_state_frmt_statement_date;
      if (!rawDate) return "Unknown Period";
      try {
         const parsed = parseISO(rawDate);
         return isNaN(parsed) ? "Unknown Period" : format(parsed, "MMM yyyy");
      } catch {
         return "Unknown Period";
      }
   };

   const handleTableDeleted = useCallback(() => {
      hasRestoredRef.current = false;
      setselectedInvoiceStatementKey(null);
      setSelectedInvoiceStatementId(null);
      setSelectedDocumentPartId(null);
      setSelectedInvoiceStatementSupName(null);
      setSelectedInvoiceStatementDate(null);
      setSelectedDocId(null);
      setListRefreshTick((t) => t + 1);
   }, []);

   // Lightweight refresh (e.g., after setting terms inline) — reload the list without
   // disturbing the open detail/selection.
   const handleTableRefresh = useCallback(() => {
      setListRefreshTick((t) => t + 1);
   }, []);

   useEffect(() => {
      if (payloadInvoiceStatements.length > 0) {
         let targetInvoiceStatement;
         let targetMonthYear;
         let targetImageIndex;

         if (hasRestoredRef.current && location.state?.selectedDocumentPartId) {
            targetInvoiceStatement = payloadInvoiceStatements.find((file) => file.doc_part_id === location.state.selectedDocumentPartId);

            if (targetInvoiceStatement) {
               targetMonthYear = getMonthYearKey(targetInvoiceStatement, activeSection);
               targetImageIndex = `${targetMonthYear}-${targetInvoiceStatement.doc_part_id}`;
            }
         } else if (selectedDocumentPartId) {
            targetInvoiceStatement = payloadInvoiceStatements.find((file) => file.doc_part_id === selectedDocumentPartId);

            if (targetInvoiceStatement) {
               targetMonthYear = getMonthYearKey(targetInvoiceStatement, activeSection);
               targetImageIndex = `${targetMonthYear}-${targetInvoiceStatement.doc_part_id}`;
            }
         }

         if (!targetInvoiceStatement && !hasRestoredRef.current) {
            targetInvoiceStatement = payloadInvoiceStatements[0];
            targetMonthYear = getMonthYearKey(targetInvoiceStatement, activeSection);
            targetImageIndex = `${targetMonthYear}-${targetInvoiceStatement.doc_part_id}`;
         }

         if (targetInvoiceStatement) {
            handleImageClick(
               targetMonthYear,
               targetImageIndex,
               targetInvoiceStatement.inv_stat_id,
               targetInvoiceStatement.doc_part_id,
               targetInvoiceStatement.sm_name,
               targetInvoiceStatement.sup_state_frmt_statement_date,
               targetInvoiceStatement.doc_id
            );
         }

         if (hasRestoredRef.current && targetInvoiceStatement) {
            requestAnimationFrame(() => {
               if (location.state?.scrollY != null) {
                  window.scrollTo(0, location.state.scrollY);
               }
            });
            hasRestoredRef.current = false;
         }
      }
   }, [payloadInvoiceStatements, selectedDocumentPartId, handleImageClick, activeSection]);

   useEffect(() => {
      const getInvoiceStatements = async () => {
         if (!activeSection) return;

         const isRecent = activeSection === "recent";
         const params = isRecent ? {} : { filter: activeSection };
         const url = isRecent
            ? "/getRecentDocuments.sz"
            : `/getInvStatements.sz?filter=${encodeURIComponent(activeSection)}`;
         const method = "GET";
         const body = { params: JSON.stringify(params) };
         const contentType = "application/json";

         setIsLoadingStatements(true);
         try {
            const { responseData } = await fetchData(url, method, body, contentType);
            if (responseData.st === 1) {
               let results = responseData.payload || [];
               if (isRecent) {
                  results = results.map((r) => ({
                     ...r,
                     statement_state: r.state === "READY" ? "ACTIVE" : r.state,
                     disabled: r.state !== "READY",
                  }));
               }
               setPayloadInvoiceStatements(results);

               if (results.length > 0) {
                  if (!(hasRestoredRef.current && location.state?.selectedDocumentPartId)) {
                     const firstItem = results.find((file) => {
                        const hasInvStatId = file?.inv_stat_id != null && file?.inv_stat_id !== "";
                        const hasStatement = hasInvStatId || (file?.statement_state && file.statement_state !== "SETUP");
                        if (activeSection === "setup") return !hasStatement;
                        if (["unreconciled", "reconciled", "location", "month"].includes(activeSection)) return hasStatement;
                        return true;
                     }) ?? results[0];
                     const monthYear = getMonthYearKey(firstItem, activeSection);
                     const imageIndex = `${monthYear}-${firstItem.doc_part_id}`;
                     setselectedInvoiceStatementKey(imageIndex);
                     setSelectedInvoiceStatementId(firstItem.inv_stat_id ?? null);
                     setSelectedDocumentPartId(firstItem.doc_part_id ?? null);
                     setSelectedInvoiceStatementSupName(firstItem.sm_name ?? null);
                     setSelectedInvoiceStatementDate(firstItem.sup_state_frmt_statement_date ?? null);
                     setSelectedDocId(firstItem.doc_id ?? null);
                  }
               } else {
                  setselectedInvoiceStatementKey(null);
                  setSelectedInvoiceStatementId(null);
                  setSelectedDocumentPartId(null);
                  setSelectedInvoiceStatementSupName(null);
                  setSelectedInvoiceStatementDate(null);
                  setSelectedDocId(null);
               }
            } else {
               if (responseData.m_en) {
                  triggerToast("warning", responseData.m_en, false, "", false);
               } else {
                  triggerToast(
                     "error",
                     "We seem to have an issue retrieving invoicing statements, please try again later.",
                     true,
                     "Issue",
                     false,
                     0
                  );
               }
            }
         } catch (error) {} finally {
            setIsLoadingStatements(false);
         }
      };

      getInvoiceStatements();
   }, [activeSection, listRefreshTick]);


   return (
      <>
         <Header />
         <div
            className="main main-app p-3 p-lg-4 p-xxl-5 invoice-statement-padding-right"
            style={{
               transition: "margin-right 0.3s ease-in-out",
               marginRight: showTable ? "0px" : "500px",
            }}
         >
            <div className="invoice-statements-sticky-header">
               <Row className="row-cols-auto g-4 g-xxl-5">
                  <Col xl>
                     <div className="d-flex align-items-center gap-2">
                        <h2 className="main-title fs-4 mb-1">Invoice Statements</h2>
                        {showTable && (
                           <span className="payee-count-chip">
                              {Number(filteredStatements.length).toLocaleString()} statements
                           </span>
                        )}
                     </div>
                  </Col>
               </Row>

               <Row className="g-3 g-lg-4">
                  <Col className="pe-0">
                     <Nav className="nav-line mb-4">
                        <Nav.Link active={activeSection === "recent"} onClick={() => handleSectionChange("recent")}>
                           Recently Uploaded
                        </Nav.Link>
                        <Nav.Link active={activeSection === "unreconciled"} onClick={() => handleSectionChange("unreconciled")}>
                           Un-reconciled
                        </Nav.Link>
                        <Nav.Link active={activeSection === "reconciled"} onClick={() => handleSectionChange("reconciled")}>
                           Reconciled
                        </Nav.Link>
                        <Nav.Link active={activeSection === "location"} onClick={() => handleSectionChange("location")}>
                           Location
                        </Nav.Link>
                        <Nav.Link active={activeSection === "month"} onClick={() => handleSectionChange("month")}>
                           Monthly
                        </Nav.Link>
                         <Nav.Link active={activeSection === "setup"} onClick={() => handleSectionChange("setup")}>
                           Review
                        </Nav.Link>
                     </Nav>
                  </Col>
               </Row>

               {["recent", "unreconciled", "reconciled", "location", "month"].includes(activeSection) && (() => {
                  const showDateRange = ["recent", "unreconciled", "reconciled", "location"].includes(activeSection);
                  const showLocations = ["recent", "unreconciled", "reconciled"].includes(activeSection);
                  const showSuppliers = true;
                  const showYears = activeSection === "month";

                  const pillBase = {
                     borderRadius: "999px",
                     padding: "3px 10px",
                     fontSize: "11px",
                     whiteSpace: "nowrap",
                     height: "26px",
                     minHeight: "26px",
                     cursor: "pointer",
                     display: "inline-flex",
                     alignItems: "center",
                     lineHeight: 1,
                     transition: "background 0.15s, border-color 0.15s",
                  };

                  const pillActive = (isActive, panelKey) => ({
                     ...pillBase,
                     background: isActive ? "#e8f0fe" : "#f1f3f5",
                     color: isActive ? "#1a73e8" : "#5f6368",
                     border: openFilterMenu === panelKey
                        ? `1px solid ${isActive ? "#1a73e8" : "#9aa0a6"}`
                        : isActive
                        ? "1px solid #aecbfa"
                        : "1px solid transparent",
                     fontWeight: isActive ? 500 : 400,
                  });

                  return (
                     <Row className="g-3 g-lg-4 mb-3">
                        <Col>
                           <div style={{ position: "relative" }}>
                              <div className="d-flex align-items-center gap-2" style={{ paddingBottom: "2px", flexWrap: "wrap" }}>
                                 <span style={{ color: "#5f6368", fontSize: "13px", display: "flex", alignItems: "center" }}>
                                    <BsFilter size={16} style={{ marginRight: "2px" }} />
                                 </span>

                                 <button
                                    type="button"
                                    onClick={resetAllFilters}
                                    style={{
                                       ...pillBase,
                                       background: "transparent",
                                       color: "#5f6368",
                                       border: "1px solid #dadce0",
                                    }}
                                 >
                                    Reset All
                                 </button>

                                 {showDateRange && (
                                    <button
                                       type="button"
                                       onClick={() => openFilterPanel("dateRange")}
                                       style={pillActive(dateRangeIsActive, "dateRange")}
                                    >
                                       {dateRangePillLabel}
                                    </button>
                                 )}

                                 {showSuppliers && (
                                    <button
                                       type="button"
                                       onClick={() => openFilterPanel("suppliers")}
                                       style={pillActive(appliedFilters.suppliers.length > 0, "suppliers")}
                                    >
                                       {severityPillLabel}
                                    </button>
                                 )}

                                 {showLocations && (
                                    <button
                                       type="button"
                                       onClick={() => openFilterPanel("locations")}
                                       style={pillActive(appliedFilters.locations.length > 0, "locations")}
                                    >
                                       {locationPillLabel}
                                    </button>
                                 )}

                                 {showYears && (
                                    <button
                                       type="button"
                                       onClick={() => openFilterPanel("years")}
                                       style={pillActive(appliedFilters.years.length > 0, "years")}
                                    >
                                       {yearPillLabel}
                                    </button>
                                 )}

                                 <div style={{ marginLeft: "auto", display: "inline-flex", border: "1px solid #dadce0", borderRadius: "999px", overflow: "hidden" }}>
                                    {[
                                       { key: "statement", label: "Statement view", Icon: FaThLarge },
                                       { key: "table", label: "Table view", Icon: FaTable },
                                    ].map((opt) => (
                                       <button
                                          key={opt.key}
                                          type="button"
                                          onClick={() => setViewMode(opt.key)}
                                          title={opt.label}
                                          aria-label={opt.label}
                                          aria-pressed={viewMode === opt.key}
                                          style={{
                                             border: "none",
                                             background: viewMode === opt.key ? "#e8f0fe" : "transparent",
                                             color: viewMode === opt.key ? "#1a73e8" : "#5f6368",
                                             display: "inline-flex",
                                             alignItems: "center",
                                             padding: "5px 12px",
                                             cursor: "pointer",
                                          }}
                                       >
                                          <opt.Icon size={14} />
                                       </button>
                                    ))}
                                 </div>
                              </div>

                              {openFilterMenu && (
                                 <div
                                    style={{
                                       position: "absolute",
                                       top: "calc(100% + 6px)",
                                       left: 0,
                                       zIndex: 1055,
                                       background: "#ffffff",
                                       border: "1px solid #e5e7eb",
                                       borderRadius: "8px",
                                       boxShadow: "0 4px 16px rgba(0,0,0,0.14)",
                                       padding: "14px 16px",
                                       maxWidth: "480px",
                                       minWidth: "280px",
                                    }}
                                 >
                                    <div className="d-flex justify-content-between align-items-center pb-2 mb-3" style={{ borderBottom: "1px solid #edf0f2" }}>
                                       <button
                                          type="button"
                                          onClick={() => resetSingleFilter(openFilterMenu)}
                                          style={{
                                             border: "none",
                                             background: "transparent",
                                             color: "#1a73e8",
                                             fontSize: "13px",
                                             padding: 0,
                                             cursor: "pointer",
                                          }}
                                       >
                                          Reset filter
                                       </button>
                                       <button
                                          type="button"
                                          aria-label="Close"
                                          onClick={() => setOpenFilterMenu(null)}
                                          style={{
                                             border: "none",
                                             background: "transparent",
                                             color: "#6c757d",
                                             padding: 0,
                                             fontSize: "14px",
                                             lineHeight: 1,
                                             cursor: "pointer",
                                          }}
                                       >
                                          <FaTimes />
                                       </button>
                                    </div>

                                    {openFilterMenu === "dateRange" && (
                                       <div>
                                          <div className="fw-semibold mb-2" style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#5f6368" }}>
                                             Date Range
                                          </div>
                                          <div className="row g-2">
                                             <div className="col-sm-6">
                                                <label className="form-label" style={{ fontSize: "11px", color: "#5f6368" }}>From</label>
                                                <input
                                                   type="date"
                                                   className="form-control form-control-sm"
                                                   value={draftFilters.dateRange.from}
                                                   onChange={(e) =>
                                                      setDraftFilters((prev) => ({
                                                         ...prev,
                                                         dateRange: { ...prev.dateRange, from: e.target.value },
                                                      }))
                                                   }
                                                />
                                             </div>
                                             <div className="col-sm-6">
                                                <label className="form-label" style={{ fontSize: "11px", color: "#5f6368" }}>To</label>
                                                <input
                                                   type="date"
                                                   className="form-control form-control-sm"
                                                   value={draftFilters.dateRange.to}
                                                   onChange={(e) =>
                                                      setDraftFilters((prev) => ({
                                                         ...prev,
                                                         dateRange: { ...prev.dateRange, to: e.target.value },
                                                      }))
                                                   }
                                                />
                                             </div>
                                          </div>
                                       </div>
                                    )}

                                    {openFilterMenu === "locations" && (
                                       <div>
                                          <div className="fw-semibold mb-2" style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#5f6368" }}>
                                             Location
                                          </div>
                                          <div className="d-flex flex-column gap-2" style={{ maxHeight: "220px", overflowY: "auto" }}>
                                             {reconcileFilterOptions.location.length === 0 ? (
                                                <span style={{ fontSize: "12px", color: "#6c757d" }}>No locations available</span>
                                             ) : (
                                                reconcileFilterOptions.location.map((option) => (
                                                   <label key={option} className="d-flex align-items-center gap-2" style={{ fontSize: "13px", cursor: "pointer" }}>
                                                      <input
                                                         type="checkbox"
                                                         checked={draftFilters.locations.includes(option)}
                                                         onChange={() => toggleDraftMultiValue("locations", option)}
                                                         style={{ width: "15px", height: "15px", flexShrink: 0 }}
                                                      />
                                                      <span>{formatLocationTypeLabel(option)}</span>
                                                   </label>
                                                ))
                                             )}
                                          </div>
                                       </div>
                                    )}

                                    {openFilterMenu === "suppliers" && (
                                       <div>
                                          <div className="fw-semibold mb-2" style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#5f6368" }}>
                                             Supplier Name
                                          </div>
                                          <div className="d-flex flex-column gap-2" style={{ maxHeight: "220px", overflowY: "auto" }}>
                                             {reconcileFilterOptions.supplierNames.length === 0 ? (
                                                <span style={{ fontSize: "12px", color: "#6c757d" }}>No suppliers available</span>
                                             ) : (
                                                reconcileFilterOptions.supplierNames.map((option) => (
                                                   <label key={option} className="d-flex align-items-center gap-2" style={{ fontSize: "13px", cursor: "pointer" }}>
                                                      <input
                                                         type="checkbox"
                                                         checked={draftFilters.suppliers.includes(option)}
                                                         onChange={() => toggleDraftMultiValue("suppliers", option)}
                                                         style={{ width: "15px", height: "15px", flexShrink: 0 }}
                                                      />
                                                      <span>{option}</span>
                                                   </label>
                                                ))
                                             )}
                                          </div>
                                       </div>
                                    )}

                                    {openFilterMenu === "years" && (
                                       <div>
                                          <div className="fw-semibold mb-2" style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#5f6368" }}>
                                             Year
                                          </div>
                                          <div className="d-flex flex-column gap-2" style={{ maxHeight: "220px", overflowY: "auto" }}>
                                             {reconcileFilterOptions.years.length === 0 ? (
                                                <span style={{ fontSize: "12px", color: "#6c757d" }}>No years available</span>
                                             ) : (
                                                reconcileFilterOptions.years.map((option) => (
                                                   <label key={option} className="d-flex align-items-center gap-2" style={{ fontSize: "13px", cursor: "pointer" }}>
                                                      <input
                                                         type="checkbox"
                                                         checked={draftFilters.years.includes(option)}
                                                         onChange={() => toggleDraftMultiValue("years", option)}
                                                         style={{ width: "15px", height: "15px", flexShrink: 0 }}
                                                      />
                                                      <span>{option}</span>
                                                   </label>
                                                ))
                                             )}
                                          </div>
                                       </div>
                                    )}

                                    <div className="pt-3 mt-3" style={{ borderTop: "1px solid #edf0f2" }}>
                                       <button
                                          type="button"
                                          className="btn btn-sm btn-primary"
                                          onClick={applyOpenFilter}
                                       >
                                          Apply
                                       </button>
                                    </div>
                                 </div>
                              )}
                           </div>
                        </Col>
                     </Row>
                  );
               })()}
            </div>

            <Row className="g-3 g-lg-4">
               <Col>
                  {showTable ? (
                     <InvoiceStatementsTable
                        rows={filteredStatements}
                        activeSection={activeSection}
                        loading={isLoadingStatements}
                        onDeleted={handleTableDeleted}
                        onChanged={handleTableRefresh}
                     />
                  ) : (
                     <InvoiceStatementIcon
                        activeSection={activeSection}
                        payloadInvoiceStatements={payloadInvoiceStatements}
                        appliedFilters={appliedFilters}
                        selectedInvoiceStatementKey={selectedInvoiceStatementKey}
                        onImageClick={handleImageClick}
                        headerRefs={headerRefs}
                     />
                  )}
               </Col>
            </Row>

            {activeGroupHeading && (
               <div className="floating-group-indicator" key={activeGroupHeading}>
                  {activeGroupHeading}
               </div>
            )}

            {activeSection === "setup" ? (
               <InvoiceStatementSetupModal
                  dpaId={selectedDocumentPartId}
                  docId={selectedDocId}
                  onDeleted={() => {
                     hasRestoredRef.current = false;
                     setselectedInvoiceStatementKey(null);
                     setSelectedInvoiceStatementId(null);
                     setSelectedDocumentPartId(null);
                     setSelectedInvoiceStatementSupName(null);
                     setSelectedInvoiceStatementDate(null);
                     setSelectedDocId(null);
                     setListRefreshTick((t) => t + 1);
                  }}
               />
            ) : !showTable ? (
               <InvoiceStatementModal
                  key="docked"
                  invStatId={selectedInvoiceStatementId}
                  dpaId={selectedDocumentPartId}
                  invStatName={selectedInvoiceStatementSupName}
                  invStatDate={selectedInvoiceStatementDate}
                  parentActiveSection={activeSection}
                  onDeleted={() => {
                     hasRestoredRef.current = false;
                     setselectedInvoiceStatementKey(null);
                     setSelectedInvoiceStatementId(null);
                     setSelectedDocumentPartId(null);
                     setSelectedInvoiceStatementSupName(null);
                     setSelectedInvoiceStatementDate(null);
                     setSelectedDocId(null);
                     setListRefreshTick((t) => t + 1);
                  }}
               />
            ) : null}
            <Footer />
         </div>
      </>
   );
}
